// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { css, cx, keyframes } from '@emotion/css';
import { Autocomplete, TextField, InputAdornment, MenuItem, Typography } from '@mui/material';
import { BiLoaderCircle, BiLogoGoogle, BiSolidMapPin } from 'react-icons/bi';
import { useMap } from '@vis.gl/react-google-maps';

import GoogleMapsElement from './GoogleMapsElement';
import { useMapPreferences } from './useMapPreferences';
import { useMapSurfaceColors } from './useMapSurfaceColors';

import { useAddressSuggestions, type AddressSuggestion } from '@hooks/useAddressSuggestions';

// TYPES ---------------------------------------------------------------- //

interface AddressSearchProps {
  mapId?: string;
  rooftopId?: number;
  placeholder?: string;
  flyToZoom?: number;
  width?: number;
  style?: React.CSSProperties;
  className?: string;
  onFlyTo?: (result: { lat: number; lng: number; suggestion: AddressSuggestion }) => void;
}

interface Option {
  placeId: string;
  mainText?: string;
  secondaryText?: string;
}

// Render GoogleMapsElement as the Autocomplete popup's paper so the list sits
// in the same panel surface as all other map controls — no extra MUI Paper box.
// Force the mode-aware panel background via inline style so the theme's
// `MuiAutocomplete-paper { background-color: transparent }` rule can't flatten it.
const DropdownPanel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>((props, ref) => {
  const { panelBackground } = useMapSurfaceColors();
  return (
    <GoogleMapsElement
      {...props}
      ref={ref}
      disablePadding
      style={{
        position: 'static',
        width: '100%',
        backgroundColor: panelBackground,
        ...props.style,
      }}
    />
  );
});
DropdownPanel.displayName = 'AddressSearchDropdownPanel';

// COMPONENT ---------------------------------------------------------------- //

const AddressSearch = ({
  mapId,
  rooftopId,
  placeholder = 'Fly to address...',
  flyToZoom = 15,
  width = 280,
  style,
  className,
  onFlyTo,
}: AddressSearchProps) => {
  const { mode } = useMapPreferences();
  const surface = useMapSurfaceColors();
  const cls = useStyles(mode, surface);
  const map = useMap(mapId);

  const { suggestions, loading, search, select, clear } = useAddressSuggestions();
  const markerRef = React.useRef<google.maps.Marker | null>(null);
  const [inputValue, setInputValue] = React.useState('');

  const options: Option[] = React.useMemo(
    () =>
      suggestions.map(s => ({
        placeId: s.placeId,
        mainText: s.mainText,
        secondaryText: s.secondaryText,
      })),
    [suggestions]
  );

  React.useEffect(() => {
    return () => {
      if (markerRef.current) markerRef.current.setMap(null);
    };
  }, []);

  const handleInputChange = (value: string, reason: string) => {
    setInputValue(value);
    if (reason === 'input') {
      search(value);
    } else {
      clear();
    }
  };

  const handleSelect = async (option: Option | null) => {
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }

    if (!option || !map || !rooftopId) return;

    const suggestion = suggestions.find(s => s.placeId === option.placeId);
    if (!suggestion) return;

    try {
      const result = await select(suggestion, rooftopId);
      if (!result.location) return;

      const lat = Number(result.location.latitude);
      const lng = Number(result.location.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      const target = { lat, lng };
      map.panTo(target);
      map.setZoom(flyToZoom);

      markerRef.current = new google.maps.Marker({
        map,
        position: target,
        title: suggestion.mainText || suggestion.fullText,
      });

      onFlyTo?.({ lat, lng, suggestion });
      setInputValue('');
    } catch (err) {
      console.error('AddressSearch: failed to resolve location', err);
    }
  };

  const disabled = !rooftopId;
  const positionStyle: React.CSSProperties = { top: 10, left: 10, width, ...style };

  return (
    <GoogleMapsElement style={positionStyle} className={className} disablePadding>
      <Autocomplete<Option, false, false, true>
        fullWidth
        freeSolo
        open={inputValue.length >= 3}
        inputValue={inputValue}
        onInputChange={(_event, value, reason) => handleInputChange(value, reason)}
        loading={loading}
        loadingText='Searching...'
        noOptionsText={disabled ? 'Select a rooftop to search' : 'No locations found'}
        options={disabled ? [] : options}
        disabled={disabled}
        slots={{ paper: DropdownPanel as never }}
        slotProps={{
          popper: {
            sx: {
              padding: 0,
              paddingTop: '6px',
              border: 'none',
              borderRadius: 0,
              backgroundColor: 'transparent',
              boxShadow: 'none',
              backdropFilter: 'none',
            },
          },
          paper: { className: cls.dropdown } as never,
        }}
        renderOption={(props, option) => (
          <MenuItem {...props} key={`address-search-${option.placeId}`}>
            <div className={cls.option}>
              <BiLogoGoogle className={cls.optionIcon} size={18} />
              <div>
                <Typography variant='body2' sx={{ fontWeight: 500, lineHeight: 1.3, color: surface.primary }}>
                  {option.mainText}
                </Typography>
                <Typography variant='caption' sx={{ color: surface.secondary }}>
                  {option.secondaryText}
                </Typography>
              </div>
            </div>
          </MenuItem>
        )}
        getOptionKey={option => (typeof option === 'object' ? option.placeId : '')}
        getOptionLabel={option =>
          typeof option === 'object' ? option.mainText || option.secondaryText || '' : String(option ?? '')
        }
        isOptionEqualToValue={(option, value) =>
          typeof option === 'object' && typeof value === 'object' && option.placeId === value.placeId
        }
        value={null}
        onChange={(_event, value) => handleSelect(typeof value === 'object' ? value : null)}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={placeholder}
            size='small'
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position='start' sx={{ ml: '8px', mr: 0 }}>
                    {loading ? (
                      <BiLoaderCircle className={cx(cls.icon, cls.iconLoader)} size={18} />
                    ) : (
                      <BiSolidMapPin className={cls.icon} size={18} />
                    )}
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'transparent',
                color: surface.primary,
                fontSize: 13,
                '& fieldset': { border: 'none' },
              },
              '& .MuiOutlinedInput-input': {
                color: surface.primary,
                '&::placeholder': { color: surface.secondary, opacity: 1 },
              },
            }}
          />
        )}
      />
    </GoogleMapsElement>
  );
};

// STYLES ---------------------------------------------------------------- //

const spin = keyframes`
  from { rotate: 0deg; }
  to { rotate: 360deg; }
`;

const useStyles = (mode: 'light' | 'dark', surface: ReturnType<typeof useMapSurfaceColors>) => {
  const hoverBg = mode === 'light' ? '#00000018' : '#ffffff20';

  return {
    option: css`
      display: flex;
      align-items: center;
      gap: 10px;
    `,
    optionIcon: css`
      flex-shrink: 0;
      color: ${surface.secondary};
    `,
    icon: css`
      color: ${surface.secondary};
    `,
    iconLoader: css`
      animation: ${spin} 1s linear infinite;
    `,
    dropdown: css`
      & .MuiAutocomplete-listbox {
        padding: 6px 0;
      }
      & .MuiAutocomplete-option {
        color: ${surface.primary};
        transition: background-color 0.1s ease-in-out;
      }
      & .MuiAutocomplete-option:hover {
        background-color: ${hoverBg} !important;
      }
      & .MuiAutocomplete-noOptions,
      & .MuiAutocomplete-loading {
        color: ${surface.secondary};
        font-size: 13px;
      }
    `,
  };
};

// EXPORT ---------------------------------------------------------------- //

export default AddressSearch;
